import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, Megaphone, Eye, MessageCircle, ShoppingBag, Search, Send, Sparkles, ListTree, Wifi, Timer, Image as ImageIcon, X, Square, RotateCcw, Loader2, Trash2, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError, fmtDate, mediaUrl } from '../../lib/api';
import { VaultPickerDialog } from './inbox/Pickers';

const STATUS_COLOR = {
  completed: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  completed_with_errors: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  running: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  paused: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  failed: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  scheduled: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  draft: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};
const STATUS_LABEL = {
  completed: 'terminée',
  completed_with_errors: 'terminée (erreurs)',
  running: 'en cours',
  paused: 'en pause',
  failed: 'échouée',
  scheduled: 'planifiée',
  draft: 'brouillon',
};
const DAILY_QUOTA = 250;

export default function MassMessages() {
  const [campaigns, setCampaigns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [manualLists, setManualLists] = useState([]);
  const [smartLists, setSmartLists] = useState([]);
  const [vault, setVault] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/mass-campaigns');
      setCampaigns(data);
    } catch (e) { toast.error(apiError(e, 'Impossible de charger les campagnes')); }
    api.get('/accounts').then(({ data }) => setAccounts(data)).catch(() => {});
    api.get('/lists/manual').then(({ data }) => setManualLists(data)).catch(() => {});
    api.get('/lists/smart').then(({ data }) => setSmartLists(data)).catch(() => {});
    api.get('/vault').then(({ data }) => setVault(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const allLists = [...manualLists.map(l => ({ ...l, kind: 'manual' })), ...smartLists.map(l => ({ ...l, kind: 'smart' }))];
  const today = new Date().toISOString().slice(0, 10);
  const sentToday = campaigns.filter(c => String(c.created_at).slice(0, 10) === today).reduce((s, c) => s + (c.sent || 0), 0);
  const remaining = Math.max(0, DAILY_QUOTA - sentToday);

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="mass-messages-page">
      <PageHeader
        title="Messages de masse"
        subtitle={`${campaigns.length} campagnes · ${remaining} envois restants aujourd'hui (max ${DAILY_QUOTA}/jour)`}
        actions={<Button data-testid="new-campaign-button" className="bg-indigo-500 hover:bg-indigo-600" onClick={() => setCreating(true)}><Plus size={16} className="mr-1.5" /> Nouvelle campagne</Button>}
        onRefresh={load}
      />

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <th className="px-5 py-3 font-semibold">Campagne</th>
              <th className="px-5 py-3 font-semibold">Compte</th>
              <th className="px-5 py-3 font-semibold">Audience</th>
              <th className="px-5 py-3 font-semibold text-right">Envoyés</th>
              <th className="px-5 py-3 font-semibold text-right">Ouverts</th>
              <th className="px-5 py-3 font-semibold text-right">Répondus</th>
              <th className="px-5 py-3 font-semibold text-right">Ventes</th>
              <th className="px-5 py-3 font-semibold">Statut</th>
              <th className="px-5 py-3 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {campaigns.map(c => {
              const acc = accounts.find(a => a.id === c.account_id);
              const includeIds = c.include_list_ids?.length ? c.include_list_ids : (c.list_id ? [c.list_id] : []);
              const lists = includeIds.map(id => allLists.find(l => l.id === id)).filter(Boolean);
              return (
                <tr key={c.id} className="hover:bg-white/[0.02]" data-testid={`campaign-row-${c.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center"><Megaphone size={13} className="text-indigo-400" /></div>
                      <div>
                        <div className="text-sm font-medium text-white">{c.name}</div>
                        <div className="text-[10px] text-slate-500">{fmtDate(c.created_at)}{c.auto_unsend_hours ? ` · auto-retrait ${c.auto_unsend_hours}h` : ''}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-300">{acc?.name || '—'}</td>
                  <td className="px-5 py-3">
                    {c.targeting === 'recently_online' ? (
                      <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-[10px]"><Wifi size={9} className="mr-1" /> Récemment en ligne</Badge>
                    ) : (
                      <div className="flex gap-1 flex-wrap">
                        {lists.slice(0, 2).map(l => <span key={l.id} className={`text-[10px] px-1.5 py-0.5 rounded border ${l.color || 'border-white/10 bg-white/5 text-slate-300'}`}>{l.emoji || ''} {l.name}</span>)}
                        {lists.length > 2 && <span className="text-[10px] text-slate-500">+{lists.length - 2}</span>}
                        {lists.length === 0 && '—'}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right text-sm text-white" data-testid={`campaign-sent-${c.id}`}>
                    {c.status === 'running' ? (
                      <span className="text-blue-300 font-semibold tabular-nums">{c.sent}/{c.total_targets || '?'}</span>
                    ) : c.sent}
                  </td>
                  <td className="px-5 py-3 text-right text-sm text-slate-300"><Eye size={11} className="inline mr-1 text-slate-500" />{c.opened}</td>
                  <td className="px-5 py-3 text-right text-sm text-slate-300"><MessageCircle size={11} className="inline mr-1 text-slate-500" />{c.replied}</td>
                  <td className="px-5 py-3 text-right text-sm font-bold text-emerald-400"><ShoppingBag size={11} className="inline mr-1" />{c.sales}</td>
                  <td className="px-5 py-3">
                    <Badge data-testid={`campaign-status-${c.id}`} className={STATUS_COLOR[c.status] || STATUS_COLOR.draft} title={c.error || undefined}>
                      {c.status === 'running' && <Loader2 size={9} className="mr-1 animate-spin" />}
                      {STATUS_LABEL[c.status] || c.status}
                    </Badge>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-start gap-1.5 flex-wrap">
                      {['draft', 'paused', 'completed_with_errors'].includes(c.status) && (
                        <Button
                          data-testid={`run-campaign-${c.id}`}
                          size="sm"
                          className="bg-emerald-500 hover:bg-emerald-600 text-white h-7 text-xs"
                          onClick={async () => {
                            if (!window.confirm(`Lancer la campagne « ${c.name} » ? Le message sera envoyé à toutes les conversations correspondantes.`)) return;
                            try {
                              const { data } = await api.post(`/mass-campaigns/${c.id}/run`);
                              if (data.queued) {
                                toast.success(`🚀 Campagne lancée — envoi à ${data.total_targets} destinataire(s) en arrière-plan`);
                              } else if (data.errors?.length) {
                                toast.info(data.errors[0]);
                              }
                              load();
                            } catch (e) { toast.error(apiError(e, 'Échec du lancement')); }
                          }}
                        ><Send size={11} className="mr-1" /> Lancer</Button>
                      )}
                      {['running', 'paused'].includes(c.status) && (
                        <Button
                          data-testid={`stop-campaign-${c.id}`}
                          size="sm" variant="outline"
                          className="h-7 text-xs border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20"
                          onClick={async () => {
                            if (!window.confirm(`Arrêter la campagne « ${c.name} » ? L'envoi s'arrêtera immédiatement.`)) return;
                            try {
                              await api.post(`/mass-campaigns/${c.id}/stop`);
                              toast.success('Campagne arrêtée');
                              load();
                            } catch (e) { toast.error(apiError(e, "Échec de l'arrêt")); }
                          }}
                        ><Square size={10} className="mr-1" /> Arrêter</Button>
                      )}
                      {['failed', 'completed', 'completed_with_errors'].includes(c.status) && (
                        <Button
                          data-testid={`reset-campaign-${c.id}`}
                          size="sm" variant="outline"
                          className="h-7 text-xs border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
                          onClick={async () => {
                            try {
                              await api.post(`/mass-campaigns/${c.id}/reset`);
                              toast.success('Campagne remise en brouillon');
                              load();
                            } catch (e) { toast.error(apiError(e, 'Échec de la réinitialisation')); }
                          }}
                        ><RotateCcw size={10} className="mr-1" /> Réinitialiser</Button>
                      )}
                      {c.status !== 'running' && (
                        <Button
                          data-testid={`delete-campaign-${c.id}`}
                          size="sm" variant="outline"
                          className="h-7 text-xs border-rose-500/20 bg-rose-500/5 text-rose-300/80 hover:bg-rose-500/15 hover:text-rose-300"
                          onClick={async () => {
                            if (!window.confirm(`Supprimer la campagne « ${c.name} » ? C'est définitif.`)) return;
                            try {
                              await api.delete(`/mass-campaigns/${c.id}`);
                              toast.success('Campagne supprimée');
                              load();
                            } catch (e) { toast.error(apiError(e, 'Suppression échouée')); }
                          }}
                        ><Trash2 size={10} className="mr-1" /> Supprimer</Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <NewCampaignDialog
        open={creating}
        accounts={accounts}
        manualLists={manualLists}
        smartLists={smartLists}
        vault={vault}
        remaining={remaining}
        onClose={() => setCreating(false)}
        onCreated={() => { setCreating(false); load(); }}
      />
    </div>
  );
}

function ListPicker({ lists, selected, onToggle, placeholder, testid }) {
  const [q, setQ] = useState('');
  const filtered = lists.filter(l => l.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="relative mb-1.5">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-600" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder} className="bg-white/5 border-white/10 text-white pl-7 h-8 text-xs" />
      </div>
      <div className="space-y-0.5 max-h-44 overflow-y-auto scrollbar-thin">
        {filtered.map(l => (
          <label key={l.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer">
            <Checkbox data-testid={`${testid}-${l.id}`} checked={selected.includes(l.id)} onCheckedChange={() => onToggle(l.id)} className="border-white/20" />
            {l.kind === 'smart' ? <Sparkles size={11} className="text-violet-400 shrink-0" /> : <ListTree size={11} className="text-slate-500 shrink-0" />}
            <span className="text-xs text-slate-200 truncate">{l.emoji || ''} {l.name}</span>
            <span className="ml-auto text-[10px] text-slate-600 whitespace-nowrap">{l.count} fans</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function NewCampaignDialog({ open, accounts, manualLists, smartLists, vault, remaining, onClose, onCreated }) {
  const [form, setForm] = useState({
    account_id: '', name: '', targeting: 'lists',
    include: [], exclude: [], body: '', variants: [],
    vault_ids: [], autoUnsend: false, unsendHours: 6,
    excludeNoReply: false, noReplyDays: 3,
  });
  const [vaultPicker, setVaultPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quota, setQuota] = useState(null);
  const [acctManual, setAcctManual] = useState(null);
  const [acctSmart, setAcctSmart] = useState(null);
  const [preview, setPreview] = useState(null); // {audience, reachable, excluded}
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (open) setForm(f => ({ ...f, account_id: f.account_id || accounts[0]?.id || '', name: '', include: [], exclude: [], body: '', variants: [], vault_ids: [], autoUnsend: false }));
  }, [open, accounts]);

  // Real server-side quota for the selected account (the server enforces it)
  useEffect(() => {
    if (!open || !form.account_id) { setQuota(null); return; }
    api.get(`/mass-campaigns/quota/${form.account_id}`).then(({ data }) => setQuota(data)).catch(() => setQuota(null));
  }, [open, form.account_id]);

  // Per-account list counts: the props are agency-wide totals that don't change with
  // the selected model. Re-fetch the lists scoped to THIS account so the displayed
  // "X fans" matches who actually receives (the send is already account-scoped).
  useEffect(() => {
    if (!open || !form.account_id) { setAcctManual(null); setAcctSmart(null); return; }
    const aid = form.account_id;
    api.get('/lists/manual', { params: { account_id: aid } }).then(({ data }) => setAcctManual(data)).catch(() => {});
    api.get('/lists/smart', { params: { account_id: aid } }).then(({ data }) => setAcctSmart(data)).catch(() => {});
  }, [open, form.account_id]);

  // Live "who will actually receive" count — resolves the SAME audience + reachable
  // filter as the real send, so an empty/wrong list shows 0 BEFORE launching.
  useEffect(() => {
    if (!open || !form.account_id) { setPreview(null); return; }
    if (form.targeting === 'lists' && form.include.length === 0) { setPreview(null); return; }
    let cancelled = false;
    setPreviewing(true);
    const t = setTimeout(() => {
      api.post('/mass-campaigns/preview-count', {
        account_id: form.account_id, targeting: form.targeting,
        include_list_ids: form.include, exclude_list_ids: form.exclude,
      }).then(({ data }) => { if (!cancelled) setPreview(data); })
        .catch(() => { if (!cancelled) setPreview(null); })
        .finally(() => { if (!cancelled) setPreviewing(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, form.account_id, form.targeting, form.include, form.exclude]);

  const allLists = [
    ...(acctManual || manualLists).map(l => ({ ...l, kind: 'manual' })),
    ...(acctSmart || smartLists).map(l => ({ ...l, kind: 'smart' })),
  ];
  const toggle = (key) => (id) => setForm(f => ({ ...f, [key]: f[key].includes(id) ? f[key].filter(x => x !== id) : [...f[key], id] }));
  const accountVault = vault.filter(v => v.account_id === form.account_id);
  const selectedMedia = form.vault_ids.map(id => vault.find(v => v.id === id)).filter(Boolean);

  const save = async () => {
    if (!form.name || !form.account_id) { toast.error('Nom de campagne et compte requis'); return; }
    if (form.targeting === 'lists' && form.include.length === 0) { toast.error("Sélectionne au moins une liste à inclure"); return; }
    if (!form.body.trim()) { toast.error('Message requis'); return; }
    setSaving(true);
    try {
      await api.post('/mass-campaigns', {
        account_id: form.account_id,
        name: form.name,
        targeting: form.targeting,
        include_list_ids: form.include,
        exclude_list_ids: form.exclude,
        body: form.body,
        variants: form.variants.map(v => v.text).filter(t => t.trim()),
        vault_ids: form.vault_ids,
        auto_unsend_hours: form.autoUnsend ? parseInt(form.unsendHours) || 6 : null,
        exclude_no_reply_days: form.excludeNoReply ? parseInt(form.noReplyDays) || 3 : null,
      });
      toast.success('Campagne créée et mise en file');
      onCreated();
    } catch (e) { toast.error(apiError(e, 'Impossible de créer la campagne')); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-5xl max-h-[90vh] overflow-y-auto scrollbar-thin p-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/5 flex-row items-center gap-3 space-y-0">
          <DialogTitle className="text-white">Nouvelle campagne</DialogTitle>
          <Badge variant="outline" className="border-white/10 bg-white/5 text-slate-400 text-[10px]" data-testid="quota-badge">
            {quota ? `${quota.remaining} / ${quota.limit} envois restants aujourd'hui pour ce compte` : `${remaining} / ${DAILY_QUOTA} envois restants aujourd'hui`}
          </Badge>
          <Badge variant="outline" className="border-emerald-500/20 bg-emerald-500/5 text-emerald-300/80 text-[10px]">
            Envoi au rythme humain (8–20s entre les fans) pour protéger le compte Telegram
          </Badge>
          {quota?.warmup && (
            <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-300 text-[10px]" data-testid="warmup-badge">
              ⚠ Nouveau compte — limite quotidienne réduite pendant la chauffe (protège du ban)
            </Badge>
          )}
        </DialogHeader>

        <div className="grid md:grid-cols-2 divide-x divide-white/5">
          {/* Audience */}
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-white font-semibold text-sm"><Megaphone size={15} className="text-indigo-400" /> Audience <span className="text-slate-500 font-normal text-xs">— qui reçoit cette campagne</span></div>

            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Compte</div>
              <select data-testid="campaign-account-select" value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white h-10 outline-none">
                {accounts.map(a => <option key={a.id} value={a.id} className="bg-[#0B0B0E]">{a.name}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Nom de la campagne</div>
              <Input data-testid="campaign-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" placeholder="ex. promo mai, relance inactifs..." />
            </div>

            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Ciblage</div>
              <div className="grid grid-cols-3 gap-1 rounded-xl bg-white/[0.03] border border-white/10 p-1">
                <button data-testid="targeting-lists" onClick={() => setForm({ ...form, targeting: 'lists' })} className={`py-2 rounded-lg text-xs flex items-center justify-center gap-1.5 ${form.targeting === 'lists' ? 'bg-indigo-500 text-white' : 'text-slate-400'}`}><ListTree size={12} /> Par listes</button>
                <button data-testid="targeting-online" onClick={() => setForm({ ...form, targeting: 'recently_online' })} className={`py-2 rounded-lg text-xs flex items-center justify-center gap-1.5 ${form.targeting === 'recently_online' ? 'bg-indigo-500 text-white' : 'text-slate-400'}`}><Wifi size={12} /> En ligne</button>
                <button data-testid="targeting-noreply" onClick={() => setForm({ ...form, targeting: 'no_reply' })} className={`py-2 rounded-lg text-xs flex items-center justify-center gap-1.5 ${form.targeting === 'no_reply' ? 'bg-indigo-500 text-white' : 'text-slate-400'}`}><MessageCircle size={12} /> Sans réponse</button>
              </div>
              {form.targeting === 'no_reply' && (
                <div className="text-[11px] text-slate-400 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                  Tous les fans de ce compte dont <span className="text-white">le dernier message est resté sans réponse</span> — pour les relancer en masse.
                </div>
              )}
              {/* Always-visible safeguard: who actually receives mass + the PeerFlood auto-pause. */}
              <div className="text-[11px] text-amber-200/80 rounded-lg border border-amber-500/20 bg-amber-500/[0.06] px-3 py-2 flex gap-2">
                <ShieldCheck size={13} className="text-amber-300 shrink-0 mt-0.5" />
                <span>Les campagnes ne partent qu'aux <span className="text-white">fans déjà dans tes conversations</span> (ceux qui t'ont écrit) — jamais à des inconnus. Si Telegram limite un compte (« Too many requests »), l'envoi <span className="text-white">se met en pause tout seul</span> : laisse le compte refroidir avant de relancer.</span>
              </div>
            </div>

            {form.targeting === 'lists' && (
              <div className="grid grid-cols-2 gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Inclure</div>
                  <ListPicker lists={allLists} selected={form.include} onToggle={toggle('include')} placeholder="Rechercher..." testid="include-list" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Exclure</div>
                  <ListPicker lists={allLists} selected={form.exclude} onToggle={toggle('exclude')} placeholder="Rechercher..." testid="exclude-list" />
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
              <Checkbox data-testid="exclude-no-reply-checkbox" checked={form.excludeNoReply} onCheckedChange={v => setForm({ ...form, excludeNoReply: v })} className="border-white/20" />
              Exclure les fans qui ont reçu un message sans réponse depuis les
              <Input type="number" value={form.noReplyDays} onChange={e => setForm({ ...form, noReplyDays: e.target.value })} className="w-14 h-7 bg-white/5 border-white/10 text-white text-xs px-2" /> derniers jours
            </label>

            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2">
              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                <Checkbox data-testid="auto-unsend-checkbox" checked={form.autoUnsend} onCheckedChange={v => setForm({ ...form, autoUnsend: v })} className="border-white/20" />
                <Timer size={12} className="text-amber-400" /> Retirer automatiquement après
                <Input type="number" value={form.unsendHours} onChange={e => setForm({ ...form, unsendHours: e.target.value })} className="w-14 h-7 bg-white/5 border-white/10 text-white text-xs px-2" disabled={!form.autoUnsend} />
                heures — messages supprimés de toutes les conversations
              </label>
            </div>
          </div>

          {/* Message */}
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white font-semibold text-sm"><Send size={14} className="text-indigo-400" /> Message <span className="text-slate-500 font-normal text-xs">— envoyé à chaque destinataire</span></div>
              <Badge variant="outline" className="border-indigo-500/30 bg-indigo-500/10 text-indigo-300 text-[10px] font-mono">{'{name}'} = prénom</Badge>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Ton message</div>
                <button data-testid="insert-name-chip" onClick={() => setForm({ ...form, body: form.body + '{name}' })} className="text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 font-mono">+ {'{name}'}</button>
              </div>
              <textarea data-testid="campaign-body-input" value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
                placeholder="Coucou {name}, comment tu vas aujourd'hui ? ..."
                className="w-full min-h-[140px] rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-indigo-500/40 resize-y" />
              <div className="text-right text-[10px] text-slate-600">{form.body.length} caractères</div>
            </div>

            {form.variants.map((v, i) => (
              <div key={v.id} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Variante {i + 1}</span>
                  <button data-testid={`delete-variant-${v.id}`} onClick={() => setForm({ ...form, variants: form.variants.filter(x => x.id !== v.id) })} className="text-slate-500 hover:text-rose-400"><X size={12} /></button>
                </div>
                <textarea value={v.text} onChange={e => setForm({ ...form, variants: form.variants.map(x => x.id === v.id ? { ...x, text: e.target.value } : x) })}
                  className="w-full min-h-[70px] rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm text-white outline-none focus:border-indigo-500/40 resize-y" />
              </div>
            ))}
            <button data-testid="add-variant-button" onClick={() => setForm({ ...form, variants: [...form.variants, { id: crypto.randomUUID(), text: '' }] })} className="w-full py-2.5 rounded-xl border border-dashed border-white/15 text-xs text-indigo-400 hover:bg-white/[0.02]">
              + Ajouter une variante <span className="text-slate-600">(optionnel)</span>
            </button>

            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Joindre un média (optionnel)</div>
              {selectedMedia.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {selectedMedia.map(v => (
                    <div key={v.id} className="relative">
                      {v.url ? <img src={mediaUrl(v.url)} alt={v.name} className="w-12 h-12 rounded-lg object-cover border border-indigo-500/40" /> : <div className="w-12 h-12 rounded-lg bg-white/5 border border-indigo-500/40 flex items-center justify-center text-[7px] text-slate-400 text-center px-1">{v.name}</div>}
                      <button onClick={() => setForm({ ...form, vault_ids: form.vault_ids.filter(x => x !== v.id) })} className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-slate-800 border border-white/20 text-white flex items-center justify-center"><X size={9} /></button>
                    </div>
                  ))}
                </div>
              )}
              <button data-testid="select-from-vault-button" onClick={() => setVaultPicker(true)} className="w-full py-2.5 rounded-xl border border-dashed border-white/15 text-xs text-slate-400 hover:text-white hover:bg-white/[0.02] flex items-center justify-center gap-1.5">
                <ImageIcon size={12} /> Choisir depuis le Vault
              </button>
              <VaultPickerDialog
                open={vaultPicker}
                onClose={() => setVaultPicker(false)}
                vault={accountVault}
                sentIds={[]}
                onPick={(vs) => {
                  setForm(f => ({ ...f, vault_ids: Array.from(new Set([...f.vault_ids, ...vs.map(v => v.id)])) }));
                  setVaultPicker(false);
                }}
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/5 flex items-center gap-3 flex-wrap">
          {form.autoUnsend && <span className="text-[11px] text-amber-400 flex items-center gap-1"><Timer size={11} /> Auto-retrait dans {form.unsendHours}h</span>}
          {previewing && <span className="text-[11px] text-slate-400 flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Calcul des destinataires…</span>}
          {!previewing && preview && (
            <span data-testid="reachable-count" className={`text-[12px] font-medium flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${preview.reachable > 0 ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/25' : 'text-rose-300 bg-rose-500/10 border-rose-500/25'}`}>
              👥 <b>{preview.reachable}</b> fan{preview.reachable > 1 ? 's' : ''} recevront
              {preview.excluded > 0 && <span className="text-slate-400">· {preview.excluded} exclu{preview.excluded > 1 ? 's' : ''} (jamais écrit)</span>}
              {preview.reachable === 0 && <span className="text-rose-300">— audience vide, rien ne partira</span>}
            </span>
          )}
          <div className="flex-1" />
          <Button variant="outline" className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={onClose}>Annuler</Button>
          <Button data-testid="campaign-save-button" className="bg-indigo-500 hover:bg-indigo-600" onClick={save} disabled={saving}>
            {saving ? 'Création...' : <>Vérifier et envoyer <Send size={13} className="ml-1.5" /></>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
